import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Table, Button, Tag, message, Space, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import api from '../utils/api';
import AppHeader from '../components/AppHeader';
import { useTheme } from '../context/ThemeContext';

const { Content } = Layout;

const DEVICE_TYPE_MAP = {
  belt_metal_detector: '皮带式金属检测机',
};

const STATUS_MAP = {
  pending: { color: 'orange', text: '待审批' },
  approved: { color: 'green', text: '已同意' },
  rejected: { color: 'red', text: '已拒绝' },
  execute_failed: { color: '#ff4d4f', text: '执行失败' }
};

const DeleteRequestsPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { isDarkMode, getBackgroundColor, getTextColor, getTextColorSecondary } = useTheme();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/delete-requests');
      const requests = res.data || [];
      
      const enrichedData = requests.map((req) => {
        return {
          ...req,
          drawing_no: req.drawing_no || '-',
          creator: req.creator || '-',
          frame_type: req.frame_type || '-',
          probe_type: req.probe_type || '-',
          requester_name: req.requester_name || '-',
          device_type_cn: DEVICE_TYPE_MAP[req.device_type] || req.device_type,
        };
      });
      
      setData(enrichedData);
    } catch (err) {
      message.error('获取数据失败');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async (id, approved) => {
    try {
      await api.put(`/delete-requests/${id}/approve`, { approved });
      message.success(approved ? '已同意删除' : '已拒绝删除');
      window.dispatchEvent(new CustomEvent('refresh-pending-count'));
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || '操作失败');
    }
  };

  const columns = [
    { title: '申请ID', dataIndex: 'id', width: 80, align: 'center' },
    { title: '图纸编号', dataIndex: 'drawing_no', width: 150, align: 'center', render: (text) => text || '-' },
    { title: '制图人', dataIndex: 'creator', width: 90, align: 'center', render: (text) => text || '-' },
    { title: '机架类型', dataIndex: 'frame_type', width: 100, align: 'center', render: (text) => text || '-' },
    { title: '探头类型', dataIndex: 'probe_type', width: 100, align: 'center', render: (text) => text || '-' },
    { 
      title: '设备类型', 
      dataIndex: 'device_type_cn',
      width: 150,
      align: 'center',
      render: (text, record) => (
        <Tooltip title={record.device_type}>
          {text || record.device_type || '-'}
        </Tooltip>
      )
    },
    { title: '申请人', dataIndex: 'requester_name', width: 100, align: 'center', render: (text) => text || '-' },
    { title: '删除原因', dataIndex: 'reason', ellipsis: true, width: 200 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      align: 'center',
            render: (status) => {
        const config = STATUS_MAP[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    { 
      title: '申请时间', 
      dataIndex: 'created_at', 
      width: 170,
      align: 'center',
      render: (text) => text ? new Date(text).toLocaleString('zh-CN', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      }).replace(/\//g, '-') : '-'
    },
    { 
      title: '审批时间', 
      dataIndex: 'approval_time', 
      width: 170,
      align: 'center',
      render: (text) => text ? new Date(text).toLocaleString('zh-CN', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      }).replace(/\//g, '-') : '-'
    },
    {
      title: '操作',
      width: 160,
      align: 'center',
      fixed: 'right',
      render: (_, record) => {
        if (record.status === 'approved' || record.status === 'rejected' || record.status === 'execute_failed') {
          return <Tag color={STATUS_MAP[record.status]?.color}>{STATUS_MAP[record.status]?.text}</Tag>;
        }
        if (record.requested_by === user.id) {
          return <Tag>自己的申请</Tag>;
        }
        return (
          <Space size={4}>
            <Button 
              type="primary" 
              size="small"
              icon={<CheckOutlined style={{ fontSize: 12 }} />}
              style={{ fontSize: 12, padding: '0 6px', height: 24 }}
              onClick={() => handleApprove(record.id, true)}
            >
              同意
            </Button>
            <Button 
              danger 
              size="small"
              icon={<CloseOutlined style={{ fontSize: 12 }} />}
              style={{ fontSize: 12, padding: '0 6px', height: 24 }}
              onClick={() => handleApprove(record.id, false)}
            >
              拒绝
            </Button>
          </Space>
        );
      }
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: getBackgroundColor() }}>
      <AppHeader />
      <Content style={{ padding: 16 }}>
        <h2 style={{ marginBottom: 16, fontSize: 18, fontWeight: 600, color: getTextColor() }}>删除申请审批</h2>
        <p style={{ color: getTextColorSecondary(), marginBottom: 16 }}>所有删除申请均通过图纸审批系统统一管理，此处展示所有删除类型的审批记录</p>
        <Table 
          columns={columns} 
          dataSource={data} 
          rowKey="id" 
          loading={loading} 
          bordered 
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </Content>
    </Layout>
  );
};

export default DeleteRequestsPage;